import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  getCompareRange,
  getPeriodRange,
  toISTDateString,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';

interface Args {
  outletId: string | 'all';
  period: PeriodKey;
}

export interface ChannelRow {
  channel: string;
  orders: number;
  sales: number;
  sharePct: number;
  metric: Metric;
}

interface Result {
  rows: ChannelRow[];
  totalSales: number;
  current: DateRange;
  previous: DateRange;
}

const CHANNEL_ORDER: Record<string, number> = {
  Swiggy: 1,
  Zomato: 2,
  'Dine In': 3,
  'Pick Up': 4,
  'Direct Delivery': 5,
  Uengage: 6,
  Other: 99,
};

async function fetchRange(outletId: string | 'all', range: DateRange) {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));
  let q = marketing
    .from('mv_channel_daily')
    .select('channel, orders, sales')
    .gte('date', start)
    .lte('date', end);
  if (outletId !== 'all') q = q.eq('outlet_id', outletId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as Array<{ channel: string; orders: number; sales: number }>;
}

export function useChannelMix({ outletId, period }: Args) {
  const current = getPeriodRange(period === 'custom' ? '7d' : period);
  const previous = getCompareRange(current, 'previous');

  return useQuery<Result>({
    queryKey: ['channel-mix', outletId, current.start.toISOString()],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([
        fetchRange(outletId, current),
        fetchRange(outletId, previous),
      ]);

      const sum = (rows: typeof curRows) => {
        const byCh = new Map<string, { orders: number; sales: number }>();
        for (const r of rows) {
          const e = byCh.get(r.channel) ?? { orders: 0, sales: 0 };
          e.orders += Number(r.orders ?? 0);
          e.sales += Number(r.sales ?? 0);
          byCh.set(r.channel, e);
        }
        return byCh;
      };

      const cur = sum(curRows);
      const prev = sum(prevRows);
      const totalSales = Array.from(cur.values()).reduce((a, b) => a + b.sales, 0);

      const rows: ChannelRow[] = Array.from(cur.entries())
        .map(([channel, c]) => {
          const p = prev.get(channel);
          return {
            channel,
            orders: c.orders,
            sales: c.sales,
            sharePct: totalSales > 0 ? (c.sales / totalSales) * 100 : 0,
            metric: computeMetric(c.sales, p?.sales ?? 0),
          };
        })
        .sort((a, b) => {
          const oa = CHANNEL_ORDER[a.channel] ?? 50;
          const ob = CHANNEL_ORDER[b.channel] ?? 50;
          if (oa !== ob) return oa - ob;
          return b.sales - a.sales;
        });

      return { rows, totalSales, current, previous };
    },
    staleTime: 60_000,
  });
}
